import React, { useEffect, useState } from "react";
import { MapPin, Loader2, Save, Coins, Lock } from "lucide-react";
import api from "../../api";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../hooks/use-toast";
import { DealLocationPicker } from "./DealLocationPicker";
import { CURRENCIES } from "../../utils/currency";
import { useLang } from "../../context/LanguageContext";

export const BranchesCard = () => {
  const { t } = useLang();
  const { user, refresh } = useAuth();
  const { toast } = useToast();
  const [branches, setBranches] = useState(() => (user?.branches && user.branches.length ? user.branches : []));
  const [currency, setCurrency] = useState(() => user?.currency || "USD");
  const [multiCurrency, setMultiCurrency] = useState(() => (user?.branches || []).some((b) => b.currency));
  const [saving, setSaving] = useState(false);
  const currencyLocked = !!user?.currencyLocked;
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/config").then((r) => setCfg(r.data)).catch(() => setCfg({})); }, []);
  const plan = user?.planLifetime ? (user.plan || "free")
    : (user?.plan && new Date(user?.planExpiresAt || 0) > new Date() ? user.plan : "free");
  const cap = !cfg ? null : (cfg.branchLimits?.[plan] ?? 1);
  const planName = { free: "Free", pro: "Pro", business_plus: "Elite" }[plan] || "Free";
  // A pin kilometres from the shop's own Google listing sends shoppers to the wrong place.
  const offPins = ((user?.placesMatch?.results) || [])
    .filter((r) => !r.cityLevel && r.status === "far" && r.distanceM >= 3000);

  const save = async () => {
    setSaving(true);
    try {
      const clean = branches.filter((b) => b.lat != null).map((b) => ({ label: b.label || "", address: b.address || "", lat: b.lat, lng: b.lng, currency: multiCurrency ? (b.currency || "") : "" }));
      const payload = { branches: clean };
      if (!currencyLocked) payload.currency = currency;
      await api.put("/my/business", payload);
      await refresh();
      toast({ title: t("br.locationsSaved"), description: t("br.newDealsWillDefault") });
    } catch (e) {
      toast({ title: t("br.couldNotSave"), description: e.response?.data?.detail || t("br.pleaseTryAgain") });
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-5 shadow-sm space-y-4" data-testid="branches-card">
      <div className="flex items-center gap-2">
        <MapPin size={18} className="text-[#16a34a]" />
        <h3 className="font-bold text-foreground">{t("br.businessLocations")}</h3>
      </div>
      {cap != null && (
        <p className="text-xs text-muted-foreground" data-testid="branch-limit-note">
          {branches.length > cap
            ? t("br.overLimit", { cap, plan: planName })
            : t("br.limitNote", { cap, plan: planName })}
        </p>
      )}

      <div>
        <label className="text-sm font-medium text-foreground block mb-1 flex items-center gap-1.5"><Coins size={14} className="text-[#16a34a]" /> {t("br.defaultCurrency")}</label>
        <select value={currency} onChange={(e) => setCurrency(e.target.value)} disabled={currencyLocked} data-testid="default-currency-select" className="w-full sm:w-64 border border-border rounded-xl px-3 py-2.5 text-sm bg-transparent outline-none focus:border-[#16a34a] text-foreground disabled:opacity-60 disabled:cursor-not-allowed">
          {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
        </select>
        {currencyLocked ? (
          <div className="mt-2 flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-500/10 px-3 py-2.5" data-testid="currency-locked-warning">
            <Lock size={15} className="text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
            <p className="text-xs text-amber-800 dark:text-amber-300">
              <span className="font-bold">{t("br.yourDefaultCurrencyIs")}</span> {t("br.itSFixedTo")} <span className="font-bold">{currency}</span> {t("br.becauseYouAlreadyHave")}
            </p>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground mt-1">{t("br.usedAsTheDefault")}</p>
        )}
      </div>

      <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer" data-testid="multi-currency-toggle">
        <input type="checkbox" checked={multiCurrency} onChange={(e) => setMultiCurrency(e.target.checked)} className="accent-[#16a34a] h-4 w-4" />
        {t("br.weReMultinationalSet")}
      </label>

      <p className="text-xs text-muted-foreground">{t("br.pinEveryBranchLocation")}</p>

      {offPins.length > 0 && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-500/10 px-3 py-2.5 space-y-1.5" data-testid="pin-mismatch-warning">
          {offPins.map((p, i) => (
            <p key={i} className="text-xs text-amber-800 dark:text-amber-300 flex items-start gap-2">
              <MapPin size={14} className="mt-0.5 shrink-0" />
              <span>{t("br.pinFarFromListing", { branch: p.label || t("br.thisPin"), distance: (p.distanceM / 1000).toFixed(1), listing: p.listing, address: p.address || "" })}</span>
            </p>
          ))}
        </div>
      )}

      <DealLocationPicker testId="branches-location-picker" branches={branches} onChange={setBranches} showHours={false} showCurrency={multiCurrency} defaultCenter={user?.lat != null ? { lat: user.lat, lng: user.lng } : undefined} />

      <button onClick={save} disabled={saving} data-testid="save-branches-btn" className="inline-flex items-center gap-2 bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white font-bold px-5 py-2.5 rounded-xl transition-colors">
        {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save locations
      </button>
    </div>
  );
};

export default BranchesCard;
